/*
  MAIN & TAYLOR — product carousel arrows, 3 Sep 2026.

  Progressive enhancement over a native scroll container: with JS off, or before this
  runs, the row is still a working horizontal scroller. All this adds is the desktop
  arrows, their disabled states, and the overflow test that decides whether to show them
  at all.
*/
(function () {
  'use strict';

  var EDGE = 2; // px tolerance; scrollLeft is fractional on zoom and HiDPI

  function step(viewport) {
    // One viewport-width per click, less a tile-edge sliver so the next click does not
    // start mid-card.
    return Math.max(viewport.clientWidth - 40, 120);
  }

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /*
    Eased scroll, done by hand.

    Measured in Chrome 151 against this page: scrollBy({behavior:'smooth'}) and CSS
    scroll-behavior:smooth both left this container at scrollLeft 0, with and without
    scroll-snap, while behavior:'auto' landed exactly on target. Rather than ship arrows
    that silently do nothing, the animation is driven here and every write to scrollLeft
    is an ordinary assignment.
  */
  function glide(viewport, delta) {
    var max = viewport.scrollWidth - viewport.clientWidth;
    var from = viewport.scrollLeft;
    var to = Math.max(0, Math.min(from + delta, max));
    if (to === from) return;

    if (reducedMotion()) { viewport.scrollLeft = to; return; }

    var DURATION = 380;
    var start = null;
    // Cancel a run still in flight so a double-click does not fight itself.
    if (viewport._mtGlide) window.cancelAnimationFrame(viewport._mtGlide);

    function frame(now) {
      if (start === null) start = now;
      var t = Math.min((now - start) / DURATION, 1);
      var eased = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t; // easeInOutQuad
      viewport.scrollLeft = from + (to - from) * eased;
      if (t < 1) {
        viewport._mtGlide = window.requestAnimationFrame(frame);
      } else {
        viewport._mtGlide = null;
      }
    }
    viewport._mtGlide = window.requestAnimationFrame(frame);
  }

  function sync(root) {
    var viewport = root.querySelector('[data-mt-carousel-viewport]');
    var prev = root.querySelector('[data-mt-carousel-prev]');
    var next = root.querySelector('[data-mt-carousel-next]');
    if (!viewport) return;

    var max = viewport.scrollWidth - viewport.clientWidth;
    root.classList.toggle('mt-carousel--overflowing', max > EDGE);
    if (prev) prev.disabled = viewport.scrollLeft <= EDGE;
    if (next) next.disabled = viewport.scrollLeft >= max - EDGE;
  }

  function wire(root) {
    if (root.dataset.mtCarouselReady === 'true') return;
    root.dataset.mtCarouselReady = 'true';

    var viewport = root.querySelector('[data-mt-carousel-viewport]');
    if (!viewport) return;

    root.addEventListener('click', function (event) {
      var button = event.target.closest('[data-mt-carousel-prev], [data-mt-carousel-next]');
      if (!button || button.disabled) return;
      var dir = button.hasAttribute('data-mt-carousel-next') ? 1 : -1;
      glide(viewport, dir * step(viewport));
    });

    viewport.addEventListener('scroll', function () {
      window.requestAnimationFrame(function () { sync(root); });
    }, { passive: true });

    // Images load after first paint and change scrollWidth, so the overflow test has to
    // run again once they land — otherwise a row that does overflow shows no arrows.
    if (window.ResizeObserver) {
      new ResizeObserver(function () { sync(root); }).observe(viewport);
    } else {
      window.addEventListener('resize', function () { sync(root); });
    }
    window.addEventListener('load', function () { sync(root); });

    sync(root);
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-mt-carousel]').forEach(wire);
  }

  document.addEventListener('DOMContentLoaded', function () { init(); });
  // Theme editor re-renders a section without a full page load.
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  // Recently-viewed fills its track from localStorage after load.
  document.addEventListener('mt:carousel:refresh', function (event) { init(event.target || document); });
  if (document.readyState !== 'loading') init();
})();
